import { useCallback, type ComponentType, type ReactNode } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import AiAnalysisPdfDownloadMenu from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisPdfDownloadMenu';
import aiContentToolsApi, {
  useGetEeatDetailsQuery
} from '@/app/ContentTools/api/aiContentAnalysisApi';
import EeatScoreCard from '@/app/ContentTools/components/ai-content-analysis/EeatScoreCard';
import { AiAnalysisReportSkeleton } from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSkeletons';
import mapEeatData from '@/app/ContentTools/utils/mapEeatData';
import { useAiDetectionSocket } from '@/app/ContentTools/hooks/useAiDetectionSocket';

type FalconCardHeaderProps = {
  className?: string;
  light?: boolean;
  title: ReactNode;
  endEl?: ReactNode;
};

type ApiErrorResponse = {
  status?: number;
  data?: {
    code?: number;
    msg?: string;
  };
};

type EeatAnalysisDetailsPanelProps = {
  analysisId: string;
  markdownReport: ComponentType<{ message: string }>;
};

const TypedFalconCardHeader =
  FalconCardHeader as ComponentType<FalconCardHeaderProps>;

const EeatAnalysisDetailsPanel = ({
  analysisId,
  markdownReport: MarkdownReport
}: EeatAnalysisDetailsPanelProps) => {
  const dispatch = useDispatch();
  const { data: detailsResponse, isLoading, error, isError } =
    useGetEeatDetailsQuery({ id: analysisId }, { skip: !analysisId });

  const detailsData = detailsResponse?.data;
  const eeatStatus = detailsData?.eeat_status;
  const isEeatPending =
    eeatStatus != null && eeatStatus !== 'finished';

  const revalidateEeatApis = useCallback(() => {
    dispatch(
      aiContentToolsApi.util.invalidateTags([
        { type: 'AiContentOverview', id: analysisId },
        { type: 'AiEeatDetails', id: analysisId }
      ])
    );
  }, [analysisId, dispatch]);

  useAiDetectionSocket({
    historyId: analysisId,
    active: Boolean(analysisId && isEeatPending),
    watchStatuses: ['eeat_status'],
    onFinished: revalidateEeatApis
  });

  const errorData = (error as ApiErrorResponse | undefined)?.data;
  const isNotFound =
    detailsResponse?.code === 400 ||
    detailsResponse?.msg === 'آیتم موردنظر پیدا نشد.' ||
    (isError &&
      ((error as ApiErrorResponse)?.status === 400 ||
        errorData?.code === 400 ||
        errorData?.msg === 'آیتم موردنظر پیدا نشد.'));

  const eeat = mapEeatData(detailsData);
  const isEeatMissing =
    Boolean(detailsData) &&
    detailsData?.eeat_status == null &&
    detailsData?.eeat == null &&
    detailsData?.eeat_report == null;

  if (isLoading) {
    return <AiAnalysisReportSkeleton />;
  }

  if (!eeat && isEeatPending) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner
            animation="border"
            variant="primary"
            className="mb-3"
            style={{ width: 28, height: 28 }}
          />
          <p className="fw-semi-bold mb-1">در حال انجام تحلیل E-E-A-T...</p>
          <p className="text-600 mb-0 fs--1">
            به‌محض آماده‌شدن نتیجه، این صفحه به‌روز می‌شود.
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (!eeat && (isNotFound || isEeatMissing)) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="fw-semi-bold mb-0">برای این مورد تحلیل E-E-A-T یافت نشد.</p>
        </Card.Body>
      </Card>
    );
  }

  if (!eeat) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="text-600 mb-0">
            {detailsResponse?.msg || 'در دریافت جزئیات تحلیل E-E-A-T خطایی رخ داد.'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col xs={12} lg={8} className="order-1 order-lg-0">
        <Card className="w-100">
          <TypedFalconCardHeader
            className="fs--1"
            light
            title={
              <span className="text-muted fw-bold fs-0">گزارش E-E-A-T</span>
            }
            endEl={
              <AiAnalysisPdfDownloadMenu
                id={analysisId}
                currentType="eeat_analysis"
              />
            }
          />
          <Card.Body>
            {eeat.message ? (
              <MarkdownReport message={eeat.message} />
            ) : (
              <p className="text-600 mb-0">گزارش متنی برای این تحلیل موجود نیست.</p>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={4} className="order-0 order-lg-1">
        <div
          className="sticky-top"
          style={{
            top: '1rem',
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto'
          }}
        >
          <EeatScoreCard data={eeat} />
        </div>
      </Col>
    </Row>
  );
};

export default EeatAnalysisDetailsPanel;
